import React, { useEffect, useState, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import DashboardLayout from "../../layouts/DashboardLayout";
import { useThemeContext } from "../../../../contexts/ThemeContext/ThemeContext";
import SubnetService from "../../../../service/SubnetService";
import GetUserInfoService from "../../../../service/GetUserInfoService";
import { safeLocaleText } from "./utils/safeLocaleText";
import PartnerB2BSearchBar from "./components/PartnerB2BSearchBar";
import PartnerB2BAddressWarningBanner from "./components/PartnerB2BAddressWarningBanner";
import PartnerB2BResultsList from "./components/PartnerB2BResultsList";
import PartnerB2BMap from "./components/PartnerB2BMap";
import PartnerB2BEmptyState from "./components/PartnerB2BEmptyState";
import PartnerDetailOverlay from "./components/PartnerDetailOverlay";
import Loading from "../../../../components/Loading/Loading";
import { Link } from "react-router-dom/cjs/react-router-dom.min";

const subnetService = new SubnetService();
const getUserInfoService = new GetUserInfoService();

const PartnerB2BPage = ({ userInfo, userTypeInfo }) => {
  const history = useHistory();
  const { subnetInfo } = useThemeContext();
  const [query, setQuery] = useState("");
  const [distance, setDistance] = useState(20);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [center, setCenter] = useState(null);
  const [userCoords, setUserCoords] = useState(null);
  const geoSuccessRef = useRef(false);

  const hasAddress = !!(userInfo?.postalCode && userInfo?.address);
  const canUseFeature = !!userCoords || hasAddress;

  const loadCenterFromAddress = useCallback(() => {
    if (!userInfo?.postalCode || geoSuccessRef.current) return;
    getUserInfoService
      .searchMyLocation({ myCep: userInfo.postalCode })
      .then((res) => {
        if (geoSuccessRef.current) return;
        if (res?.result?.latitude != null && res?.result?.longitude != null) {
          const coords = { lat: res.result.latitude, lng: res.result.longitude };
          setCenter(coords);
          setUserCoords(coords);
        }
      })
      .catch(() => {});
  }, [userInfo?.postalCode]);

  useEffect(() => {
    if (hasAddress) loadCenterFromAddress();
    if (!navigator.geolocation) return;
    if (geoSuccessRef.current) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        geoSuccessRef.current = true;
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        setCenter(coords);
        setUserCoords(coords);
      },
      (err) => {
        // err.code: 1=PERMISSION_DENIED, 2=POSITION_UNAVAILABLE, 3=TIMEOUT
        if (process.env.NODE_ENV === "development") {
          console.warn("[PartnerB2B] Geolocation failed:", err.code, err.message);
        }
        // loadCenterFromAddress já é chamado no início do efeito quando hasAddress
      },
      {
        enableHighAccuracy: false, // faster; desktops often lack GPS
        timeout: 20000,
        maximumAge: 60000, // allow cached position (1 min)
      },
    );
  }, [hasAddress, loadCenterFromAddress]);

  const handleSearch = () => {
    if (!userCoords) return;
    const token = localStorage.getItem("__extraconomy_token");
    if (!token) return;
    setLoading(true);
    setHasSearched(false);
    const payload = { distance, query };
    if (userCoords && userCoords.lat !== 0 && userCoords.lng !== 0) {
      payload.currentLatitude = userCoords.lat;
      payload.currentLongitude = userCoords.lng;
    }
    subnetService
      .geolocalizedSearch(token, payload)
      .then((response) => {
        if (response?.messageCode === 200000 && response?.result?.results) {
          setResults(response.result.results);
        } else {
          setResults([]);
        }
        setHasSearched(true);
      })
      .catch(() => {
        setResults([]);
        setHasSearched(true);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!subnetInfo) {
    return (
      <DashboardLayout>
        <nav
          className="flex align-items-center gap-2 mb-2 text-sm"
          style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
          aria-label="Breadcrumb"
        >
          <span style={{ color: "var(--theme-text, #171717)" }}>
            {safeLocaleText("partnerB2bTitle", "Partner B2B")}
          </span>
        </nav>
        <div
          className="p-4 border-round"
          style={{
            backgroundColor: "var(--theme-border, #eaeaea)",
            color: "var(--theme-text-muted, #5C5C5C)",
          }}
        >
          <p className="m-0">
            {safeLocaleText(
              "partnerB2bSubnetOnly",
              "This page is only available for subnet members.",
            )}
          </p>
          <button
            type="button"
            className="p-button p-button-text mt-2"
            style={{ color: "var(--theme-primary, #1DAF61)" }}
            onClick={() => history.push("/dashboard")}
          >
            {safeLocaleText("userDashboard", "Dashboard")}
          </button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <nav
        className="flex align-items-center gap-2 mb-2 text-sm"
        style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
        aria-label="Breadcrumb"
      >
        <span style={{ color: "var(--theme-text, #171717)" }}>
          {safeLocaleText("partnerB2bTitle", "Partner B2B")}
        </span>
      </nav>
      <div className="mb-4">
        <h1 className="text-xl font-semibold m-0" style={{ color: "var(--theme-text, #171717)" }}>
          {safeLocaleText("partnerB2bTitle", "Partner B2B")}
        </h1>
        <p className="text-sm m-0 mt-1" style={{ color: "var(--theme-text-muted, #5C5C5C)" }}>
          {safeLocaleText(
            "partnerB2bSubtitle",
            "Find and connect with strategic partners in your region.",
          )}
        </p>
        <Link
          to="/dashboard-partner-stores"
          className="text-sm m-0 mt-1"
          style={{ color: "var(--theme-primary, #1DAF61)" }}
        >
          {safeLocaleText("partnerStoresMenuLabel", "Lojas conveniadas")}
        </Link>
      </div>

      {!canUseFeature && <PartnerB2BAddressWarningBanner />}

      <PartnerB2BSearchBar
        query={query}
        onQueryChange={setQuery}
        distance={distance}
        onDistanceChange={setDistance}
        onSearch={handleSearch}
        loading={loading}
        disabled={!userCoords}
      />

      {loading && (
        <div className="flex justify-content-center align-items-center min-h-20rem">
          <Loading classNameIcon="text-8xl" />
        </div>
      )}

      {!loading && canUseFeature && (
        <div className="grid">
          <div className="col-12 md:col-4 lg:col-3">
            <PartnerB2BResultsList
              results={results}
              hasSearched={hasSearched}
              distance={distance}
              onSelect={setSelectedPartner}
              userCoords={userCoords}
            />
          </div>
          <div className="col-12 md:col-8 lg:col-9">
            <PartnerB2BMap
              center={center}
              markers={results}
              onMarkerClick={setSelectedPartner}
              height="600px"
            />
            {hasSearched && results.length === 0 && (
              <div className="mt-3">
                <PartnerB2BEmptyState />
              </div>
            )}
          </div>
        </div>
      )}

      {selectedPartner && (
        <PartnerDetailOverlay
          partner={selectedPartner}
          userCenter={center}
          onClose={() => setSelectedPartner(null)}
        />
      )}
    </DashboardLayout>
  );
};

export default PartnerB2BPage;
